import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthShell from "@/pages/auth/AuthShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/app", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get("error");
    if (err) setError(err);
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back to Vvvoraa.");
      navigate(location.state?.from || "/app", { replace: true });
    } else {
      setError(res.error);
    }
  };

  const forgot = async () => {
    if (!email.trim()) {
      setError("Enter your email above, then tap Forgot password.");
      return;
    }
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If an account exists, a reset link has been sent.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AuthShell
      title="Sign in to Vvvoraa"
      subtitle="Access your Control Center."
      footer={<>New to Vvvoraa? <Link to="/signup" className="text-[#4F8CFF] hover:underline" data-testid="link-signup">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
        {error && (
          <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-300" data-testid="login-error">
            {error}
          </div>
        )}
        <div>
          <Label htmlFor="email" className="text-slate-300">Email</Label>
          <Input
            id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF]"
            placeholder="you@company.com" data-testid="login-email" autoComplete="email"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <button type="button" onClick={forgot} className="text-xs text-[#4F8CFF] hover:underline" data-testid="forgot-password-link">
              Forgot password
            </button>
          </div>
          <div className="relative mt-1.5">
            <Input
              id="password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-white/[0.03] border-white/12 text-white placeholder:text-slate-500 focus-visible:ring-[#4F8CFF] pr-10"
              placeholder="••••••••" data-testid="login-password" autoComplete="current-password"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" aria-label="Toggle password visibility">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full font-semibold" data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
        </Button>

        <div className="relative py-1 text-center">
          <span className="relative z-10 bg-[#05080D] px-3 text-xs text-slate-500">or</span>
          <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
        </div>

        <Button
          type="button" variant="outline"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/app";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full h-11 bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full"
          data-testid="google-signin"
        >
          Continue with Google
        </Button>

        <p className="text-center text-xs text-slate-500">
          Demo: founder@vora.ai / VoraDemo2026!
        </p>
      </form>
    </AuthShell>
  );
}
